// src/api/authInterceptor.ts

import type {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'

import { tokenService } from './tokenService'
import { refreshCoordinator } from './refreshCoordinator'

import { useAuthFacade } from '@/auth/facade/authFacade'
import { appAuth } from '@/auth/platform/appAuth'

import { logger } from '@myorg/type-lib'

async function refreshWebSession(baseURL: string) {
  logger.log(
    '[Interceptor] 🌐 Web 401 → refresh session'
  )

  await axios.post(
    `${baseURL}/api/client/refresh`,
    {},
    {
      withCredentials: true,

      /**
       * refresh 自己失敗不要再進 interceptor
       */
      _skipGlobalError: true,
    }
  )
}

async function refreshAppToken(baseURL: string) {
  logger.log(
    '[Interceptor] 📱 App 401 → refresh token'
  )

  const tokens = await tokenService(baseURL)

  await appAuth.setTokens(tokens)

  return tokens
}

async function performRefresh(baseURL: string) {
  const isApp = Capacitor.isNativePlatform()

  if (isApp) {
    return refreshAppToken(baseURL)
  }

  await refreshWebSession(baseURL)

  return null
}

export function setupAuthInterceptor(
  api: AxiosInstance,
  baseURL: string
) {
  logger.log('SETUP INTERCEPTOR', baseURL)
  logger.log('AUTH INTERCEPTOR VERSION 20260622')
  /**
   * =========================================
   * 🚀 REQUEST INTERCEPTOR
   * =========================================
   */
  api.interceptors.request.use(async config => {
    const isApp = Capacitor.isNativePlatform()

    if (isApp) {
      const token = await appAuth.getAccessToken()

      if (token) {
        config.headers = config.headers ?? {}

        config.headers.Authorization =
          `Bearer ${token}`
      }

      config.withCredentials = false
    } else {
      /**
       * Web 依靠 Session Cookie
       */
      config.withCredentials = true
    }

    return config
  })

  /**
   * =========================================
   * 📥 RESPONSE INTERCEPTOR
   * =========================================
   */
  api.interceptors.response.use(
    res => {
      logger.log(
        '[Interceptor] ✅ success',
        res.config.url,
        res.status
      )

      return res
    },

    async (error: AxiosError) => {
      logger.error(
        '[Interceptor] ❌ axios error',
        {
          url: error.config?.url,
          method: error.config?.method,
          message: error.message,
          code: error.code,
          status: error.response?.status,
          data: error.response?.data,
        }
      )

      const original =
        error.config as InternalAxiosRequestConfig

      if (!original) {
        logger.warn(
          '[Interceptor] No original config'
        )

        throw error
      }

      /**
       * refresh endpoint 不處理
       */
      if (
        original._skipGlobalError ||
        original.url?.includes('/api/client/refresh')
      ) {
        logger.log(
          '[Interceptor] Skip refresh handling',
          original.url
        )

        throw error
      }

      /**
       * 非 401 不處理
       */
      if (error.response?.status !== 401) {
        logger.log(
          '[Interceptor] Ignore non-401',
          error.response?.status
        )

        throw error
      }

      logger.log(
        '[Interceptor] 🔐 Handle 401',
        original.url
      )

      /**
       * 同一 request 只 retry 一次
       */
      if (original._retry) {
        logger.warn(
          '[Interceptor] Already retried',
          original.url
        )

        throw error
      }

      original._retry = true

      /**
       * 已有人 refresh
       */
      if (!refreshCoordinator.beginRefresh()) {
        logger.log(
          '[Interceptor] ⏳ waiting refresh'
        )

        await refreshCoordinator.waitForRefresh()

        logger.log(
          '[Interceptor] 🔁 retry after waiting',
          original.url
        )

        return api(original)
      }

      /**
       * 我負責 refresh
       */
      try {
        logger.log(
          '[Interceptor] 🚀 start refresh'
        )

        const tokens =
          await performRefresh(baseURL)

        logger.log(
          '[Interceptor] ✅ refresh success'
        )

        /**
         * App refresh 成功後更新 Authorization
         */
        if (tokens?.access_token) {
          original.headers =
            original.headers ?? {}

          original.headers.Authorization =
            `Bearer ${tokens.access_token}`
        }

        refreshCoordinator.resolveRefresh()

        logger.log(
          '[Interceptor] 🔁 retry original request',
          original.url
        )

        return api(original)
      } catch (err) {
        logger.error(
          '[Interceptor] 💥 refresh failed',
          err
        )

        refreshCoordinator.rejectRefresh(
          err as AxiosError
        )

        const auth = useAuthFacade()

        await refreshCoordinator.runLogout(
          async () => {
            logger.warn(
              '[Interceptor] 🚪 logout'
            )

            await auth.logout({
              isForce: true,
            })
          }
        )

        throw err
      }
    }
  )
}